// spec-10 — the Ask God saved-sessions REST client. Thin wrappers over the
// shared api() helper (which attaches the Bearer token); the server owns naming,
// ownership (savedByEmail) and updated_at-desc ordering. JSON is camelCase.
import { api } from "../api/client";
import type { AgentMessage } from "./graph";
import type { Item } from "./AgentSessionContext";

/** A saved chat's metadata (list rows + save responses carry no transcript). */
export interface SessionMeta {
  id: string;
  name: string;
  savedByEmail: string;
  createdAt: string;
  updatedAt: string;
  model: string;
}

/** A saved chat in full — the metadata plus the rehydratable transcript. */
export interface SessionFull extends SessionMeta {
  messages: AgentMessage[];
  uiItems: Item[];
}

/** The save payload (create + update share it). `model` is optional server-side. */
export interface SaveSessionInput {
  name: string;
  messages: AgentMessage[];
  uiItems: Item[];
  model?: string;
}

/** GET all saved chats — already sorted updated_at desc by the server. */
export async function listSessions(): Promise<SessionMeta[]> {
  const { sessions } = await api<{ sessions: SessionMeta[] }>("/api/agent/sessions");
  return sessions;
}

/** GET one saved chat in full (throws ApiError(404) if missing). */
export async function getSession(id: string): Promise<SessionFull> {
  const { session } = await api<{ session: SessionFull }>(
    `/api/agent/sessions/${encodeURIComponent(id)}`,
  );
  return session;
}

/** POST a new saved chat → its metadata. */
export async function createSession(input: SaveSessionInput): Promise<SessionMeta> {
  const { session } = await api<{ session: SessionMeta }>("/api/agent/sessions", {
    method: "POST",
    body: JSON.stringify(input),
  });
  return session;
}

/** PUT (overwrite) an existing saved chat → its updated metadata. */
export async function updateSession(
  id: string,
  input: SaveSessionInput,
): Promise<SessionMeta> {
  const { session } = await api<{ session: SessionMeta }>(
    `/api/agent/sessions/${encodeURIComponent(id)}`,
    { method: "PUT", body: JSON.stringify(input) },
  );
  return session;
}

/** DELETE a saved chat (204, no body). */
export async function deleteSession(id: string): Promise<void> {
  await api<null>(`/api/agent/sessions/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}
